import { createSelector, createSlice } from "@reduxjs/toolkit";

const initialState = {
  deviceToken: {},
  devices: [],
  selectedDevice: {},
  currentDevice: {},
  deviceLocation: [],
  singleDeviceLocation: {},
  selectedDevicesAll: [],
  alerts: [],
  securebootDevices: [],
  devicesession: { data: [], meta: {}, filterKey: "all" },
  devicesessionStarted: { data: [], meta: {}, filterKey: "all" },
  devicesessionByFilter: {
    all: { data: [], meta: {} },
  },
};

const clearSessionData = (state) => {
  return {
    ...state,
    devicesession: { data: [], meta: {}, filterKey: "all" },
    devicesessionStarted: { data: [], meta: {}, filterKey: "all" },
    devicesessionByFilter: {
      all: { data: [], meta: {} },
    },
  };
};

const name = "devices";
const parentSelector = (state) => state?.[name];

const devicesSlice = createSlice({
  name: name,
  initialState: initialState,
  reducers: {
    resetDevices: () => {
      return initialState;
    },
    setDevicesToken: (state, action) => {
      return {
        ...state,
        deviceToken: action.payload,
      };
    },
    setDevices: (state, action) => {
      return {
        ...state,
        devices: action.payload,
      };
    },
    setSelectedDevice: (state, action) => {
      const prevOS = state?.selectedDevice?.systemDetails?.os;
      const nextOS = action.payload?.systemDetails?.os;
      const shouldClearSessions = prevOS && nextOS && prevOS !== nextOS;

      if (shouldClearSessions) {
        return {
          ...clearSessionData(state),
          selectedDevice: action.payload,
        };
      }

      return {
        ...state,
        selectedDevice: action.payload,
      };
    },
    setCurrentDevice: (state, action) => {
      return {
        ...state,
        currentDevice: action.payload,
      };
    },
    setDeviceLocation: (state, action) => {
      return {
        ...state,
        deviceLocation: action.payload,
      };
    },
    setSingleDeviceLocation: (state, action) => {
      return {
        ...state,
        singleDeviceLocation: action.payload,
      };
    },
    setSelectedDevicesAll: (state, action) => {
      return {
        ...state,
        selectedDevicesAll: action.payload,
      };
    },
    setAlerts: (state, action) => {
      return {
        ...state,
        alerts: action.payload,
      };
    },
    setSecurebootDevices: (state, action) => {
      return {
        ...state,
        securebootDevices: action.payload,
      };
    },
    setDevicesession: (state, action) => {
      const { filterKey = "all" } = action.payload || {};
      const nextDevicesession = {
        ...action.payload,
        filterKey,
      };
      return {
        ...state,
        devicesession: nextDevicesession,
        devicesessionByFilter: {
          ...state.devicesessionByFilter,
          [filterKey]: {
            data: nextDevicesession.data || [],
            meta: nextDevicesession.meta || {},
          },
        },
      };
    },
    setDeviceStartedSessions: (state, action) => {
      const { filterKey = "all" } = action.payload || {};
      const nextDevicesession = {
        ...action.payload,
        filterKey,
      };
      return {
        ...state,
        devicesessionStarted: nextDevicesession,
      };
    },
    appendDevicesession: (state, action) => {
      const nextFilterKey =
        action.payload?.filterKey || state?.devicesession?.filterKey || "all";
      const existing =
        state?.devicesessionByFilter?.[nextFilterKey] || { data: [], meta: {} };
      const nextData = [(action.payload?.session || {}), ...(existing.data || [])];
      const totalFromMeta = Number(existing.meta?.total) || existing.data?.length || 0;
      const nextMeta = {
        ...existing.meta,
        total: totalFromMeta + 1,
      };

      return {
        ...state,
        devicesession: {
          data: nextData,
          meta: nextMeta,
          filterKey: nextFilterKey,
        },
        devicesessionByFilter: {
          ...state.devicesessionByFilter,
          [nextFilterKey]: {
            data: nextData,
            meta: nextMeta,
          },
        },
      };
    },

  },
});

const getDeviceToken = createSelector(parentSelector, (state) => {
  return state?.deviceToken;
});

const getDevices = createSelector(parentSelector, (state) => {
  return state?.devices;
});

const getSelectedDevice = createSelector(parentSelector, (state) => {
  return state?.selectedDevice;
});

const getCurrentDevice = createSelector(parentSelector, (state) => {
  return state?.currentDevice;
});

const getDeviceLocation = createSelector(parentSelector, (state) => {
  return state?.deviceLocation;
});

const getSingleDeviceLocation = createSelector(parentSelector, (state) => {
  return state?.singleDeviceLocation;
});

const getSelectedDevicesAll = createSelector(parentSelector, (state) => {
  return state?.selectedDevicesAll;
});

const getAlerts = createSelector(parentSelector, (state) => {
  return state?.alerts;
});

const getSecurebootDevices = createSelector(parentSelector, (state) => {
  return state?.securebootDevices;
});

const getDeviceSession = createSelector(
  parentSelector,
  (_, filterKey = "all") => filterKey,
  (state, filterKey) => {
    const entry = state?.devicesessionByFilter?.[filterKey];
    if (entry) {
      return entry;
    }
    return state?.devicesession || { data: [], meta: {}, filterKey: "all" };
  }
);

const getDeviceStartedSessions = createSelector(parentSelector, (state) => {
  return state?.devicesessionStarted || { data: [], meta: {}, filterKey: "all" };
});

const getBlockChainInfo = createSelector(parentSelector, (state) => {
  return state?.blockChainInfo;
});

const getBlockChainBlock = createSelector(parentSelector, (state) => {
  return state?.blockChainBlock;
});

const actions = {
  ...devicesSlice.actions,
};

const selectors = {
  getDeviceToken,
  getDevices,
  getSelectedDevice,
  getCurrentDevice,
  getDeviceLocation,
  getSingleDeviceLocation,
  getSelectedDevicesAll,
  getAlerts,
  getBlockChainInfo,
  getBlockChainBlock,
  getSecurebootDevices,
  getDeviceSession,
  getDeviceStartedSessions,
};

export const devicesActions = { actions, selectors };

export default devicesSlice;
